import { MigrationStrategies } from 'rxdb';

import ProformaInvoiceEntity, {
  PROFORMA_INVOICE_STATUS,
  PAID_STATUS_SOURCE,
} from './ProformaInvoiceEntity';

type ProformaInvoiceFields = Omit<
  ProformaInvoiceEntity,
  'uuid' | 'workspaceId' | 'reviewRequiredAt' | 'paidStatusSource' | 'totalPaid' | 'balanceDue'
>;

const V2_DEFAULTS: ProformaInvoiceFields = {
  invoiceId: null,
  number: null,
  priceProposalId: null,
  priceProposalReference: null,
  contactName: null,
  contactId: null,
  title: null,
  subtotalPrice: null,
  priceWithDiscount: null,
  taxValue: null,
  priceWithTax: null,
  description: null,
  discountType: null,
  discountAmount: null,
  items: [],
  settings: null,
  status: PROFORMA_INVOICE_STATUS.PENDING_PAYMENT,
  createdAt: null,
  updatedAt: null,
  dueDate: null,
  createdBy: null,
  updatedBy: null,
  priceProposalOverrideFields: false,
  priceProposalOverrideSettings: false,
};

/**
 * Keeps the invoices already stored by the read-only list (schema v1) instead of
 * dropping them, so the list stays populated while the stream refetches them
 * with the new fields (see ENTITY_RESYNC_VERSIONS in streamUpdateV3/storage.ts).
 *
 * Fields missing from a v1 document get their defaults; fields it does have
 * (including RxDB's own `_meta`, `_rev`, ...) are kept.
 */
export const migrateProformaInvoiceToV2 = (oldDoc) => {
  const migrated = { ...V2_DEFAULTS };

  Object.keys(oldDoc).forEach((key) => {
    if (oldDoc[key] !== undefined && oldDoc[key] !== null) {
      migrated[key] = oldDoc[key];
    }
  });

  return {
    ...migrated,
    items: Array.isArray(oldDoc.items) ? oldDoc.items : [],
  };
};

/**
 * Adds the server review mark (LAE-752). Stored invoices start unmarked; the
 * stream resync (ENTITY_RESYNC_VERSIONS) brings marks the server already set.
 */
export const migrateProformaInvoiceToV3 = (oldDoc) => ({
  ...oldDoc,
  reviewRequiredAt: oldDoc.reviewRequiredAt ?? null,
});

/**
 * Adds the payment totals (LAE-768). Stored invoices read as manual with nothing
 * paid until the stream resync (ENTITY_RESYNC_VERSIONS) brings the real values.
 */
export const migrateProformaInvoiceToV4 = (oldDoc) => ({
  ...oldDoc,
  paidStatusSource: oldDoc.paidStatusSource ?? PAID_STATUS_SOURCE.MANUAL,
  totalPaid: oldDoc.totalPaid ?? 0,
  balanceDue: oldDoc.balanceDue ?? null,
});

/**
 * Strategies for versions above the shared dbVersion (1). They are merged with
 * the shared strategies in DbManager.addCollections.
 */
const proformaInvoiceMigrationStrategies: MigrationStrategies = {
  2: migrateProformaInvoiceToV2,
  3: migrateProformaInvoiceToV3,
  4: migrateProformaInvoiceToV4,
};

export default proformaInvoiceMigrationStrategies;
